/**
 * Sidebar menu configuration — single source of truth for the app's
 * navigation tree. Split out of Layout.js to keep rendering logic lean.
 *
 * Shape:
 *   [{ title, badge, badgeColor, isParent?, items: [...] }]
 *
 * Item kinds:
 *   - leaf:   { path, icon, label }
 *   - nested: { label, icon, isNested: true, children: [leaf, ...] }
 *   - sub-section (only under `isParent: true`): { title, badge, badgeColor, items: [leaf, ...] }
 */
import {
  LayoutDashboard,
  Building2,
  DollarSign,
  BarChart3,
  Code2,
  Target,
  Layers,
  Users,
  ShoppingCart,
  TrendingUp,
  Settings,
  Database,
  Upload,
  Wallet,
  PieChart,
  Calendar,
  CalendarDays,
  Clock,
  Play,
  CreditCard,
  Activity,
  GitBranch,
  Package,
  UserCog,
  UserCheck,
  UsersRound,
  Plus,
  FolderOpen,
  Link,
  Link2,
  MapPin,
  Truck,
  Tag,
  FileText,
  RotateCcw,
  Shield,
  Waves,
  FileCheck,
  Network,
  Briefcase,
  Send,
  Wand2,
  Workflow,
  Boxes,
  Landmark,
  Inbox,
  TrendingDown,
  ShieldCheck,
  ClipboardCheck,
  Calculator,
  BookOpen,
  Lock,
  Factory,
  Cog,
  ClipboardList,
  Wrench,
  Gauge,
  CalendarClock,
} from 'lucide-react';

export const sidebarSections = [
  {
    title: 'LOW-CODE',
    badge: 'LC',
    badgeColor: 'bg-pink-100 text-pink-700',
    items: [
      { path: '/config/doctype-builder', icon: Boxes, label: 'DocType Builder' },
      { path: '/config/workflows', icon: Workflow, label: 'Workflows' },
      { path: '/config/custom-fields', icon: Wand2, label: 'Custom Fields' },
      { path: '/config/report-builder', icon: BarChart3, label: 'Report Builder' },
      { path: '/config/client-scripts', icon: Code2, label: 'Client Scripts' },
      { path: '/config/print-formats', icon: FileText, label: 'Print Formats' },
    ],
  },
  {
    title: 'ROUTINES',
    badge: 'RUN',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/routines', icon: Play, label: 'Process Runners' },
      { path: '/approvals', icon: Inbox, label: 'My Approvals' },
    ],
  },
  {
    title: 'ANALYTICS',
    badge: 'AN',
    badgeColor: 'bg-sky-100 text-sky-700',
    items: [
      { path: '/analytics', icon: Activity, label: 'Overview' },
      { path: '/analytics/p2p', icon: TrendingDown, label: 'Procure-to-Pay' },
      { path: '/analytics/otc', icon: TrendingUp, label: 'Order-to-Cash' },
    ],
  },
  {
    title: 'ACCESS CONTROL',
    badge: 'AC',
    badgeColor: 'bg-red-100 text-red-700',
    adminOnly: true,
    items: [
      { path: '/access-control', icon: Shield, label: 'Users & Permissions' },
    ],
  },
  {
    title: 'AUDIT',
    badge: 'AU',
    badgeColor: 'bg-amber-100 text-amber-700',
    auditOnly: true,
    items: [
      { path: '/audit-log', icon: ClipboardList, label: 'Audit Log' },
    ],
  },
  {
    title: 'SELF SERVICE',
    badge: 'ME',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/portal', icon: UserCog, label: 'My Profile' },
    ],
  },
  {
    title: 'ENTERPRISE STRUCTURE',
    badge: 'ES',
    badgeColor: 'bg-orange-100 text-orange-700',
    isParent: true,
    items: [
      {
        title: 'SALES & DISTRIBUTION',
        badge: 'SD',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/sales-distribution', icon: ShoppingCart, label: 'Sales Organizations' },
          { path: '/sales-distribution?tab=divisions', icon: Target, label: 'Divisions' },
          { path: '/sales-distribution?tab=dist-channels', icon: TrendingUp, label: 'Distribution Channels' },
          { path: '/sales-distribution?tab=sales-areas', icon: Layers, label: 'Sales Areas' },
          { path: '/sales-distribution?tab=sales-offices', icon: Building2, label: 'Sales Offices' },
          { path: '/sales-distribution?tab=sales-groups', icon: Users, label: 'Sales Groups' },
          { path: '/sales-distribution?tab=departure-zones', icon: GitBranch, label: 'Departure Zones' },
          { path: '/sales-distribution?tab=shipping-points', icon: Package, label: 'Shipping Points' },
          { path: '/assignments', icon: Settings, label: 'Assignments' },
          { path: '/bulk-upload', icon: Upload, label: 'Bulk Upload' },
        ],
      },
      {
        title: 'MASTER DATA',
        badge: 'MD',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/master-data', icon: Database, label: 'Languages' },
          { path: '/master-data?tab=countries', icon: Target, label: 'Countries' },
          { path: '/master-data?tab=currencies', icon: DollarSign, label: 'Currencies' },
        ],
      },
      {
        title: 'COMPANY',
        badge: 'CO',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/company', icon: Building2, label: 'Companies' },
          { path: '/company?tab=company-codes', icon: BarChart3, label: 'Company Codes' },
        ],
      },
      {
        title: 'FINANCE',
        badge: 'FI',
        badgeColor: 'bg-green-100 text-green-700',
        items: [
          { path: '/finance?tab=credit-control-areas', icon: Wallet, label: 'Credit Control Areas' },
          { path: '/finance?tab=business-areas', icon: Target, label: 'Business Areas' },
          { path: '/finance?tab=consolidated-business-areas', icon: Layers, label: 'Consolidated Business Areas' },
          { path: '/finance?tab=functional-areas', icon: Settings, label: 'Functional Areas' },
          { path: '/finance?tab=segments', icon: PieChart, label: 'Segments' },
          { path: '/finance?tab=chart-of-accounts', icon: Database, label: 'Chart of Accounts' },
          { path: '/finance?tab=fiscal-year-variants', icon: Calendar, label: 'Fiscal Year Variants' },
          { path: '/finance/fi-document-types', icon: FileText, label: 'FI Document Types' },
          { path: '/finance/fi-number-ranges', icon: Database, label: 'FI Number Ranges' },
          { path: '/finance/journal-vouchers', icon: BookOpen, label: 'Journal Vouchers (FB50)' },
          { path: '/finance/gl-line-items', icon: FileText, label: 'GL Line Items (FBL3N)' },
          { path: '/finance/trial-balance', icon: BarChart3, label: 'Trial Balance' },
          { path: '/finance-assignments', icon: Settings, label: 'Finance Assignments' },
        ],
      },
      {
        title: 'CONTROLLING',
        badge: 'CO',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/controlling?tab=operating-concerns', icon: Target, label: 'Operating Concerns' },
          { path: '/controlling?tab=controlling-areas', icon: Layers, label: 'Controlling Areas' },
          { path: '/controlling/cost-center-hierarchy', icon: GitBranch, label: 'Cost Center Hierarchy' },
          { path: '/controlling?tab=cost-centers', icon: Building2, label: 'Cost Centers' },
          { path: '/controlling?tab=profit-center-groups', icon: Users, label: 'Profit Center Groups' },
          { path: '/controlling?tab=profit-centers', icon: TrendingUp, label: 'Profit Centers' },
          { path: '/controlling?tab=internal-orders', icon: Activity, label: 'Internal Orders' },
          { path: '/controlling?tab=statistical-key-figures', icon: BarChart3, label: 'Statistical Key Figures' },
          { path: '/controlling-assignments', icon: Settings, label: 'Controlling Assignments' },
        ],
      },
      {
        title: 'MATERIAL MANAGEMENT',
        badge: 'MM',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/material-management?tab=holiday-calendars', icon: Calendar, label: 'Holiday Calendars' },
          { path: '/material-management?tab=factory-calendars', icon: Activity, label: 'Factory Calendars' },
          { path: '/material-management?tab=plants', icon: Building2, label: 'Plants' },
          { path: '/material-management?tab=storage-locations', icon: Package, label: 'Storage Locations' },
          { path: '/material-management?tab=purchasing-organizations', icon: Building2, label: 'Purchasing Organizations' },
          { path: '/material-management?tab=purchasing-groups', icon: Users, label: 'Purchasing Groups' },
          { path: '/material-management-assignments', icon: Settings, label: 'MM Assignments' },
        ],
      },
      {
        title: 'WAREHOUSE MGMT',
        badge: 'EWM',
        badgeColor: 'bg-orange-100 text-orange-700',
        items: [
          { path: '/ewm?tab=warehouses', icon: Package, label: 'Warehouses' },
          { path: '/ewm?tab=storage-types', icon: Package, label: 'Storage Types' },
          { path: '/ewm?tab=storage-sections', icon: Package, label: 'Storage Sections' },
          { path: '/ewm?tab=storage-bins', icon: Package, label: 'Storage Bins' },
          { path: '/ewm-assignments', icon: Settings, label: 'EWM Assignments' },
        ],
      },
    ],
  },
  {
    title: 'VENDOR MASTER',
    badge: 'VM',
    badgeColor: 'bg-amber-100 text-amber-700',
    items: [
      { path: '/vendors/create', icon: Plus, label: 'Create Vendor' },
      { path: '/vendors', icon: Users, label: 'View Vendors' },
      {
        label: 'Config',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/vendor-master?tab=account-groups', icon: Users, label: 'Account Groups' },
          { path: '/vendor-master?tab=number-ranges', icon: Database, label: 'Number Ranges' },
          { path: '/vendor-master?tab=partner-functions', icon: UsersRound, label: 'Partner Functions' },
          { path: '/vendor-master?tab=configurations', icon: Settings, label: 'Account Group Config' },
        ],
      },
    ],
  },
  {
    title: 'MATERIAL MASTER',
    badge: 'MM',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/material-creation', icon: Plus, label: 'Create Material' },
      { path: '/view-materials', icon: Package, label: 'View Materials' },
      {
        label: 'Config',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/number-range', icon: Database, label: 'Number Range' },
          { path: '/material-master?tab=material-types', icon: Package, label: 'Material Types' },
          { path: '/unit-of-measure', icon: Activity, label: 'Unit of Measure' },
          { path: '/material-group', icon: Layers, label: 'Material Group' },
          { path: '/item-category-group', icon: Target, label: 'Item Category Group' },
          { path: '/weight-unit', icon: Activity, label: 'Weight Unit' },
          { path: '/transportation-group', icon: TrendingUp, label: 'Transportation Group' },
          { path: '/loading-group', icon: Upload, label: 'Loading Group' },
          { path: '/availability-check', icon: Calendar, label: 'Availability Check' },
          { path: '/account-assignment-group', icon: FolderOpen, label: 'Account Assignment Group' },
          { path: '/mrp-type', icon: Settings, label: 'MRP Type' },
          { path: '/mrp-controller', icon: UserCog, label: 'MRP Controller' },
          { path: '/lot-size', icon: Package, label: 'Lot Size' },
          { path: '/procurement-type', icon: ShoppingCart, label: 'Procurement Type' },
          { path: '/scheduling-margin-key', icon: Calendar, label: 'Scheduling Margin Key' },
          { path: '/period-indicator', icon: Calendar, label: 'Period Indicator' },
          { path: '/forecast-model', icon: Calendar, label: 'Forecast Model' },
          { path: '/selection-procedure', icon: Calendar, label: 'Selection Procedure' },
          { path: '/task-list-usage', icon: Calendar, label: 'Task List Usage' },
          { path: '/valuation-category', icon: Calendar, label: 'Valuation Category' },
          { path: '/account-category-reference', icon: Calendar, label: 'Account Category Reference' },
          { path: '/valuation-type', icon: Calendar, label: 'Valuation Type' },
          { path: '/valuation-type-category-assignment', icon: Calendar, label: 'Valuation Type Assignment' },
          { path: '/valuation-class', icon: Calendar, label: 'Valuation Class' },
        ],
      },
    ],
  },
  {
    title: 'CUSTOMER MASTER',
    badge: 'CM',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/customer-creation', icon: Plus, label: 'Create Customer' },
      { path: '/view-customers', icon: FolderOpen, label: 'View Customers' },
      {
        label: 'Config',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/customer-account-group', icon: Users, label: 'Account Groups' },
          { path: '/customer-number-range', icon: Database, label: 'Number Ranges' },
          { path: '/customer-account-group-config', icon: Settings, label: 'Account Group Config' },
          { path: '/customer-partner-function', icon: UserCheck, label: 'Partner Functions' },
          { path: '/customer-partner-determination-procedure', icon: GitBranch, label: 'Partner Det. Procedures' },
          { path: '/customer-pdp-partner-function', icon: Settings, label: 'Procedure Functions' },
          { path: '/customer-account-group-pdp', icon: Link2, label: 'Assign Procedure to AG' },
          { path: '/customer-account-group-partner-function', icon: Link, label: 'Direct Partner Assignment' },
          { path: '/customer-sales-district', icon: MapPin, label: 'Sales Districts' },
          { path: '/customer-group', icon: UsersRound, label: 'Customer Groups' },
          { path: '/customer-delivery-priority', icon: Truck, label: 'Delivery Priorities' },
          { path: '/customer-terms-of-payment', icon: CreditCard, label: 'Terms of Payment' },
          { path: '/customer-incoterms', icon: Package, label: 'Incoterms' },
          { path: '/customer-shipping-conditions', icon: Truck, label: 'Shipping Conditions' },
        ],
      },
    ],
  },
  {
    title: 'PROCURE-TO-PAY',
    badge: 'P2P',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/p2p/dashboard', icon: LayoutDashboard, label: 'P2P Dashboard' },
      { path: '/p2p/document-types', icon: Settings, label: 'Document Types' },
      { path: '/p2p/number-ranges', icon: Tag, label: 'Number Ranges' },
      { path: '/vendor-quotes', icon: FileText, label: 'Vendor Quotations' },
    ],
  },
  {
    title: 'HUMAN RESOURCES',
    badge: 'HCM',
    badgeColor: 'bg-pink-100 text-pink-700',
    items: [
      { path: '/hcm', icon: LayoutDashboard, label: 'HCM Dashboard' },
      {
        label: 'Personnel Admin',
        icon: Users,
        isNested: true,
        children: [
          { path: '/hcm/employees', icon: Users, label: 'Employees' },
          { path: '/hcm/employees/create', icon: Plus, label: 'Create Employee' },
          { path: '/hcm/personnel-actions', icon: FileText, label: 'Personnel Actions' },
        ],
      },
      {
        label: 'Org Management',
        icon: Network,
        isNested: true,
        children: [
          { path: '/hcm/om', icon: LayoutDashboard, label: 'OM Dashboard' },
          { path: '/hcm/om/org-units', icon: Building2, label: 'Org Units' },
          { path: '/hcm/om/jobs', icon: Briefcase, label: 'Jobs' },
          { path: '/hcm/om/positions', icon: UserCheck, label: 'Positions' },
          { path: '/hcm/om/org-chart', icon: Network, label: 'Org Chart' },
        ],
      },
      {
        label: 'Time Management',
        icon: Clock,
        isNested: true,
        children: [
          { path: '/hcm/time', icon: LayoutDashboard, label: 'Time Dashboard' },
          { path: '/hcm/time/absences', icon: CalendarDays, label: 'Absences' },
          { path: '/hcm/time/attendances', icon: Clock, label: 'Attendances' },
          { path: '/hcm/time/employee-quotas', icon: TrendingUp, label: 'Employee Quotas' },
          { path: '/hcm/time/config', icon: Settings, label: 'Time Config' },
        ],
      },
      {
        label: 'Payroll',
        icon: DollarSign,
        isNested: true,
        children: [
          { path: '/hcm/payroll', icon: LayoutDashboard, label: 'Payroll Dashboard' },
          { path: '/hcm/payroll/runs', icon: Play, label: 'Payroll Runs' },
          { path: '/hcm/payroll/results', icon: FileText, label: 'Payroll Results' },
          { path: '/hcm/payroll/bank-transfers', icon: CreditCard, label: 'Bank Transfers' },
          { path: '/hcm/payroll/config', icon: Settings, label: 'Payroll Config' },
          { path: '/hcm/payroll/gl-mapping', icon: Link2, label: 'GL Mapping' },
          { path: '/hcm/payroll/gl-posting', icon: Send, label: 'GL Posting' },
        ],
      },
      {
        label: 'Configuration',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/hcm/config/enterprise-structure', icon: Building2, label: 'Enterprise Structure' },
          { path: '/hcm/config/pay-scale', icon: DollarSign, label: 'Pay Scale' },
          { path: '/hcm/config/wage-types', icon: Wallet, label: 'Wage Types' },
          { path: '/hcm/config/employee-id', icon: Settings, label: 'Employee ID Format' },
        ],
      },
    ],
  },
  {
    title: 'PROJECT SYSTEMS',
    badge: 'PS',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/ps', icon: LayoutDashboard, label: 'PS Dashboard' },
      {
        label: 'Configuration',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/ps/config/project-profiles', icon: Settings, label: 'Project Profiles' },
          { path: '/ps/config/wbs-profiles', icon: Layers, label: 'WBS Profiles' },
          { path: '/ps/config/network-profiles', icon: Network, label: 'Network Profiles' },
          { path: '/ps/config/status-profiles', icon: Activity, label: 'Status Profiles' },
          { path: '/ps/config/planning-profiles', icon: Target, label: 'Planning Profiles' },
          { path: '/ps/config/budget-profiles', icon: DollarSign, label: 'Budget Profiles' },
        ],
      },
      {
        label: 'Projects',
        icon: FolderOpen,
        isNested: true,
        children: [
          { path: '/ps/projects', icon: FolderOpen, label: 'Project List' },
          { path: '/ps/projects/create', icon: Plus, label: 'Create Project' },
          { path: '/ps/templates', icon: FileText, label: 'Project Templates' },
        ],
      },
      {
        label: 'Planning & Budgeting',
        icon: Calculator,
        isNested: true,
        children: [
          { path: '/ps/planning', icon: LayoutDashboard, label: 'Planning Dashboard' },
          { path: '/ps/planning/budget', icon: DollarSign, label: 'Budget Management' },
          { path: '/ps/planning/cost-plans', icon: Calculator, label: 'Cost Planning' },
          { path: '/ps/planning/comparison', icon: BarChart3, label: 'Plan vs Actual' },
          { path: '/ps/planning/commitments', icon: FileText, label: 'Commitments' },
          { path: '/ps/investment-programs', icon: Wallet, label: 'Investment Programs' },
        ],
      },
      {
        label: 'Execution & Control',
        icon: Play,
        isNested: true,
        children: [
          { path: '/ps/execution', icon: LayoutDashboard, label: 'Execution Dashboard' },
          { path: '/ps/execution/networks', icon: Network, label: 'Networks' },
          { path: '/ps/execution/activities', icon: Activity, label: 'Activities' },
          { path: '/ps/execution/confirmations', icon: FileCheck, label: 'Confirmations' },
          { path: '/ps/execution/materials', icon: Package, label: 'Material Reservations' },
        ],
      },
      {
        label: 'Closing & Reporting',
        icon: Lock,
        isNested: true,
        children: [
          { path: '/ps/closing', icon: LayoutDashboard, label: 'Closing Dashboard' },
          { path: '/ps/closing/periods', icon: Calendar, label: 'Period Locks' },
          { path: '/ps/closing/settlement', icon: DollarSign, label: 'Settlement' },
          { path: '/ps/closing/wip', icon: TrendingUp, label: 'WIP Calculation' },
          { path: '/ps/closing/closure', icon: Lock, label: 'Project Closure' },
          { path: '/ps/reports', icon: FileText, label: 'Reports' },
        ],
      },
      {
        label: 'FI Integration',
        icon: DollarSign,
        isNested: true,
        children: [
          { path: '/ps/integration', icon: LayoutDashboard, label: 'Integration Dashboard' },
          { path: '/ps/integration/account-determination', icon: Settings, label: 'Account Determination' },
          { path: '/ps/integration/fi-documents', icon: FileText, label: 'FI Documents' },
          { path: '/ps/integration/wip-posting', icon: TrendingUp, label: 'WIP Posting' },
        ],
      },
    ],
  },
  {
    title: 'MANUFACTURING',
    badge: 'PP',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/manufacturing', icon: LayoutDashboard, label: 'PP Dashboard' },
      { path: '/manufacturing/production-orders', icon: ClipboardList, label: 'Production Orders' },
      { path: '/manufacturing/production-orders/create', icon: Plus, label: 'Create Order' },
      {
        label: 'Master Data',
        icon: Settings,
        isNested: true,
        children: [
          { path: '/manufacturing/work-centers', icon: Cog, label: 'Work Centers' },
          { path: '/manufacturing/boms', icon: Layers, label: 'Bills of Material' },
          { path: '/manufacturing/routings', icon: GitBranch, label: 'Routings' },
          { path: '/manufacturing/number-ranges', icon: Database, label: 'Number Ranges' },
        ],
      },
    ],
  },
  {
    title: 'PRE-SALES',
    badge: 'PS',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/presales/inquiries', icon: FileText, label: 'Inquiries' },
      { path: '/presales/inquiries/create', icon: Plus, label: 'Create Inquiry' },
      { path: '/presales/quotations', icon: DollarSign, label: 'Quotations' },
      { path: '/presales/quotations/create', icon: Plus, label: 'Create Quotation' },
    ],
  },
  {
    title: 'ORDER-TO-CASH',
    badge: 'OTC',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/otc/dashboard', icon: LayoutDashboard, label: 'OTC Dashboard' },
      { path: '/otc/sales-orders', icon: ShoppingCart, label: 'Sales Orders' },
      { path: '/otc/sales-orders/create', icon: Plus, label: 'Create Sales Order' },
      { path: '/otc/deliveries', icon: Truck, label: 'Deliveries' },
      { path: '/otc/deliveries/create', icon: Plus, label: 'Create Delivery' },
      { path: '/otc/billing', icon: DollarSign, label: 'Billing Documents' },
      { path: '/otc/billing/create', icon: Plus, label: 'Create Billing' },
      { path: '/otc/billing-blocks', icon: Shield, label: 'Billing Blocks' },
      { path: '/otc/milestone-billing', icon: Target, label: 'Milestone Billing' },
      { path: '/otc/intercompany-billing', icon: Building2, label: 'Intercompany Billing' },
      { path: '/otc/credit-exposure', icon: TrendingUp, label: 'Credit Exposure' },
      { path: '/otc/atp-check', icon: Package, label: 'ATP Check' },
      { path: '/otc/returns', icon: GitBranch, label: 'Returns Orders' },
      { path: '/otc/credit-memos', icon: FileText, label: 'Credit Memos' },
      { path: '/otc/sales-performance', icon: BarChart3, label: 'Sales Performance' },
      { path: '/otc/profitability', icon: TrendingUp, label: 'Profitability Analysis' },
    ],
  },
  {
    title: 'ACCOUNTS RECEIVABLE',
    badge: 'AR',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    items: [
      { path: '/ar/open-items', icon: FileText, label: 'Customer Open Items' },
      { path: '/ar/payments', icon: DollarSign, label: 'Incoming Payments' },
      { path: '/ar/payments/create', icon: Plus, label: 'Post Payment' },
      { path: '/ar/aging-report', icon: Activity, label: 'AR Aging Report' },
    ],
  },
  {
    title: 'ACCOUNTS PAYABLE',
    badge: 'AP',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/ap/payments', icon: DollarSign, label: 'Vendor Payments' },
      { path: '/ap/payment-proposal', icon: Play, label: 'Payment Run (F110)' },
      { path: '/ap/open-items', icon: FileText, label: 'Vendor Open Items' },
      { path: '/ap/aging', icon: Activity, label: 'AP Aging Report' },
    ],
  },
  {
    title: 'INVENTORY MGMT',
    badge: 'MM',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/inventory', icon: LayoutDashboard, label: 'Inventory Dashboard' },
      { path: '/inventory/stock-overview', icon: Package, label: 'Stock Overview (MMBE)' },
      { path: '/inventory/material-documents', icon: Database, label: 'Material Documents' },
      { path: '/inventory/initial-stock', icon: Upload, label: 'Initial Stock (561)' },
      { path: '/inventory/stock-transfer', icon: TrendingUp, label: 'Stock Transfer' },
      { path: '/inventory/grir-reconciliation', icon: Activity, label: 'GR/IR Reconciliation' },
      { path: '/inventory/accounting-documents', icon: DollarSign, label: 'Accounting Docs' },
    ],
  },
  {
    title: 'WAREHOUSE',
    badge: 'WH',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/warehouse/pick-lists', icon: FileText, label: 'Pick Lists' },
      { path: '/warehouse/shipments', icon: Truck, label: 'Shipments' },
      { path: '/warehouse/wave-picking', icon: Waves, label: 'Wave Picking' },
      { path: '/warehouse/pod', icon: FileCheck, label: 'Proof of Delivery' },
      { path: '/warehouse/routes', icon: MapPin, label: 'Route Scheduling' },
    ],
  },
  {
    title: 'VENDOR RETURNS',
    badge: 'VR',
    badgeColor: 'bg-rose-100 text-rose-700',
    items: [{ path: '/vendor-returns', icon: RotateCcw, label: 'Returns to Vendor' }],
  },
  {
    title: 'RELEASE STRATEGY',
    badge: 'WF',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/release/strategies', icon: Shield, label: 'Strategies' },
      { path: '/release/inbox', icon: FileText, label: 'Approval Inbox' },
    ],
  },
  {
    title: 'PRICING',
    badge: 'SD',
    badgeColor: 'bg-orange-100 text-orange-700',
    items: [
      { path: '/pricing/condition-types', icon: Tag, label: 'Condition Types' },
      { path: '/pricing/condition-tables', icon: Database, label: 'Condition Tables' },
      { path: '/pricing/access-sequences', icon: GitBranch, label: 'Access Sequences' },
      { path: '/pricing/pricing-procedures', icon: Layers, label: 'Pricing Procedures' },
      { path: '/pricing/condition-records', icon: DollarSign, label: 'Condition Records' },
      { path: '/pricing/simulator', icon: Activity, label: 'Pricing Simulator' },
    ],
  },
  {
    title: 'ASSET ACCOUNTING',
    badge: 'FI-AA',
    badgeColor: 'bg-amber-100 text-amber-700',
    items: [
      { path: '/assets', icon: Landmark, label: 'Asset Register' },
      { path: '/assets/asset-classes', icon: Layers, label: 'Asset Classes' },
      { path: '/assets/depreciation-run', icon: TrendingDown, label: 'Depreciation Run' },
    ],
  },
  {
    title: 'QUALITY MANAGEMENT',
    badge: 'QM',
    badgeColor: 'bg-teal-100 text-teal-700',
    items: [
      { path: '/quality', icon: ShieldCheck, label: 'QM Dashboard' },
      { path: '/quality/inspection-lots', icon: ClipboardCheck, label: 'Inspection Lots' },
      { path: '/quality/inspection-plans', icon: FileCheck, label: 'Inspection Plans' },
      { path: '/quality/inspection-types', icon: Settings, label: 'Inspection Types' },
      { path: '/quality/notifications', icon: FileCheck, label: 'Quality Notifications' },
      { path: '/quality/certificates', icon: FileCheck, label: 'Quality Certificates' },
    ],
  },
  {
    title: 'PLANT MAINTENANCE',
    badge: 'PM',
    badgeColor: 'bg-sky-100 text-sky-700',
    items: [
      { path: '/maintenance', icon: Wrench, label: 'PM Dashboard' },
      { path: '/maintenance/equipment', icon: Cog, label: 'Equipment' },
      { path: '/maintenance/functional-locations', icon: MapPin, label: 'Functional Locations' },
      { path: '/maintenance/notifications', icon: ClipboardList, label: 'Notifications' },
      { path: '/maintenance/orders', icon: FileText, label: 'Work Orders' },
      { path: '/maintenance/task-lists', icon: ClipboardCheck, label: 'Task Lists' },
      { path: '/maintenance/maintenance-plans', icon: CalendarClock, label: 'Maintenance Plans' },
      { path: '/maintenance/measuring-points', icon: Gauge, label: 'Measuring Points' },
    ],
  },
];

// Default localStorage shape for expandedSections. LOW-CODE starts expanded
// so the framework tools (DocType Builder / Workflows / Custom Fields) are
// immediately discoverable; everything else collapsed.
export const defaultExpandedSections = {
  ...Object.fromEntries(
    sidebarSections.flatMap((s) =>
      s.isParent && s.items ? [[s.title, false], ...s.items.map((sub) => [sub.title, false])] : [[s.title, false]],
    ),
  ),
  'LOW-CODE': true,
};

// Default localStorage shape for nested-item expansion.
export const defaultExpandedNested = {
  Config: false,
  'Personnel Admin': false,
  Configuration: false,
  'Org Management': false,
  'Time Management': false,
  Payroll: false,
  Projects: false,
  'Planning & Budgeting': false,
  'Execution & Control': false,
};

/**
 * Map from URL-path prefix → section/nested to auto-expand when navigating.
 * Keeps the auto-expand side-effect data-driven (out of Layout.js).
 */
export const pathToExpandRules = [
  { prefix: '/config/doctype-builder', section: 'LOW-CODE' },
  { prefix: '/config/workflows', section: 'LOW-CODE' },
  { prefix: '/config/custom-fields', section: 'LOW-CODE' },
  { prefix: '/config/report-builder', section: 'LOW-CODE' },
  { prefix: '/config/client-scripts', section: 'LOW-CODE' },
  { prefix: '/config/print-formats', section: 'LOW-CODE' },
  { prefix: '/doc/', section: 'LOW-CODE' },
  { prefix: '/sales-distribution', section: 'SALES & DISTRIBUTION' },
  { prefix: '/assignments', section: 'SALES & DISTRIBUTION' },
  { prefix: '/bulk-upload', section: 'SALES & DISTRIBUTION' },
  { prefix: '/master-data', section: 'MASTER DATA' },
  { prefix: '/company', section: 'COMPANY' },
  { prefix: '/finance', section: 'FINANCE' },
  { prefix: '/controlling', section: 'CONTROLLING' },
  { prefix: '/material-management', section: 'MATERIAL MANAGEMENT' },
  { prefix: '/ewm', section: 'WAREHOUSE MGMT' },
  { prefix: '/hcm/payroll', section: 'HUMAN RESOURCES', nested: 'Payroll' },
  { prefix: '/hcm/time', section: 'HUMAN RESOURCES', nested: 'Time Management' },
  { prefix: '/hcm/om', section: 'HUMAN RESOURCES', nested: 'Org Management' },
  { prefix: '/hcm/employees', section: 'HUMAN RESOURCES', nested: 'Personnel Admin' },
  { prefix: '/hcm/personnel', section: 'HUMAN RESOURCES', nested: 'Personnel Admin' },
  { prefix: '/hcm', section: 'HUMAN RESOURCES' },
  { prefix: '/ps/config', section: 'PROJECT SYSTEMS', nested: 'Configuration' },
  { prefix: '/ps/projects', section: 'PROJECT SYSTEMS', nested: 'Projects' },
  { prefix: '/ps/templates', section: 'PROJECT SYSTEMS', nested: 'Projects' },
  { prefix: '/ps/planning', section: 'PROJECT SYSTEMS', nested: 'Planning & Budgeting' },
  { prefix: '/ps/investment-programs', section: 'PROJECT SYSTEMS', nested: 'Planning & Budgeting' },
  { prefix: '/ps/execution', section: 'PROJECT SYSTEMS', nested: 'Execution & Control' },
  { prefix: '/ps', section: 'PROJECT SYSTEMS' },
  { prefix: '/manufacturing/work-centers', section: 'MANUFACTURING', nested: 'Master Data' },
  { prefix: '/manufacturing/boms', section: 'MANUFACTURING', nested: 'Master Data' },
  { prefix: '/manufacturing/routings', section: 'MANUFACTURING', nested: 'Master Data' },
  { prefix: '/manufacturing/number-ranges', section: 'MANUFACTURING', nested: 'Master Data' },
  { prefix: '/manufacturing', section: 'MANUFACTURING' },
  { prefix: '/inventory', section: 'INVENTORY MGMT' },
  { prefix: '/assets', section: 'ASSET ACCOUNTING' },
  { prefix: '/quality', section: 'QUALITY MANAGEMENT' },
  { prefix: '/maintenance', section: 'PLANT MAINTENANCE' },
  { prefix: '/pricing', section: 'PRICING' },
  { prefix: '/otc', section: 'ORDER-TO-CASH' },
  { prefix: '/ar', section: 'ACCOUNTS RECEIVABLE' },
  { prefix: '/p2p', section: 'PROCURE-TO-PAY' },
];
